import React, { useState } from 'react';
import { graphql, Link } from 'gatsby';
import Seo from '../components/seo';
import NewLayout from '../components/new_layout';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import CardPost from '../components/v2023/UI/Card/card-post';
import Opinions from '../components/v2023/Opinions/opinions';
import Certifications from '../components/v2023/Certifications/certifications';
import Identity from '../components/v2023/Identity/identity';
import { getFirstText } from '../utils/gatsby-helpers';
import { CarouselTouch } from '../components/v2024/Carousel/carousel-touch';

const KnowledgeCenter = ({ data, pageContext }) => {
	const lang = pageContext.langKey;
	const page = data.page;
	const webinars = data.webinars.edges;
	const caseStudies = data.caseStudies.edges;
	const whitePapers = data.whitePapers.edges;
	const posts = data.posts.edges;
	const guides = data.guides.edges;
	const resources = data.resources.edges;

	const pathBlog = lang === 'en' ? '/blog' : `/${lang}/blog`;
	const pathWebinars = lang === 'en' ? '/webinars' : `/${lang}/webinars`;
	const pathWhitePapers = lang === 'en' ? '/ebooks-whitepapers' : `/${lang}/ebooks-whitepapers`;
	const pathCaseStudy = lang === 'en' ? '/case-studies' : '/es/casos-exito';
	const pathGuides = lang === 'en' ? '' : `/${lang}`;
	const pathEventsAndWebinars = lang === 'en' ? '/eventos-and-webinars' : '/es/eventos-and-webinars';
	const pathResources = lang === 'en' ? '/knowledge-center/resources' : '/es/knowledge-center/recursos';

	const [selectedIndexSolution, setSelectedIndexSolution] = useState(0);

	let [limitCaseStudies, setLimitCaseStudies] = useState(3);
	let [limitWebinars, setLimitWebinars] = useState(4);
	let [limitWhitePapers, setLimitWhitePapers] = useState(4);
	let [limitPosts, setLimitPosts] = useState(4);
	let [limitGuides, setLimitGuides] = useState(4);
	let [limitResources, setLimitResources] = useState(4);

	const webinarsArray = webinars
		.filter((webinar) => webinar.node.important)
		.map((webinar, index) => ({ ...webinar, key: `webinar-${index}` }))
		.map((webinar) => ({ ...webinar, node: { ...webinar.node, url: `${pathWebinars}/${webinar.node.url}` } }));
	const whitePapersArray = whitePapers
		.filter((whitePaper) => whitePaper.node.important)
		.map((whitePaper, index) => ({ ...whitePaper, key: `white-paper-${index}` }))
		.map((whitepaper) => ({
			...whitepaper,
			node: { ...whitepaper.node, url: `${pathWhitePapers}/${whitepaper.node.url}` },
		}));
	const guidesArray = guides
		.filter((guide) => guide.node.important)
		.map((guide, index) => ({...guide, key: `guide-${index}`}))
		.map((guide) => ({
			...guide,
			node: {...guide.node, url: `${pathGuides}/${guide.node.url}`}
		}));
	const resourcesArray = resources
		.filter((resource) => resource.node.important)
		.map((resource, index) => ({...resource, key: `resources-${index}`}))
		.map((resource) => ({
			...resource,
			node: {...resource.node, url: `${pathResources}/${resource.node.url}`}
		}));
	const carouselArray = webinarsArray.concat(whitePapersArray).concat(guidesArray).concat(resourcesArray).sort((item1, item2) => {
		const order1 = item1.node.order;
		const order2 = item2.node.order;
		if (order1 === null) {
			return 1;
		}
		if (order2 === null) {
			return -1;
		}
		return item1.node.order - item2.node.order;
	});

	let carouselItems = [];
	carouselItems = carouselArray.map((item) => item.key);

	const showMoreWebinars = () => {
		if (limitWebinars < webinars.length) {
			setLimitWebinars(limitWebinars + 4);
		}
	};

	const showMoreWhitePapers = () => {
		if (limitWhitePapers < whitePapers.length) {
			setLimitWhitePapers(limitWhitePapers + 4);
		}
	};

	const showMorePosts = () => {
		if (limitPosts < posts.length) {
			setLimitPosts(limitPosts + 4);
		}
	};

	const showMoreGuides = () => {
		if (limitGuides < guides.length) {
			setLimitGuides(limitGuides + 4);
		}
	};

	return (
		<NewLayout pageContext={pageContext}>
			<Seo
				lang={pageContext.langKey}
				title={page.seo.title}
				description={page.seo.meta_description}
				image={page.seo?.image_2?.localFile?.publicURL}
				translates={pageContext.translates}
			/>

			<main className="main">
				<div className="main__section main__section--100 white-section brown">
					<div className="container" data-aos="fade-up">
						<div className="container__text">
							<h1 className="hero hero--thin">{page.title}</h1>
						</div>
						<CarouselTouch
							lang={lang}
							slides={carouselArray}
							showArrows
							showIndicators
							useCardPost
							hideShowMore
						/>
					</div>
				</div>

				<div className="main__section main__section--100 color-section purple" id="case-studies">
					<div className="container">
						<ReactMarkdown children={page.description} rehypePlugins={[rehypeRaw]} />
						<div className="grid-lg-3">
							{caseStudies.slice(0, limitCaseStudies).map((item, index) => (
								<CardPost
									key={index}
									name={item.node.title}
									image={item.node.logo_new}
									url={`${pathCaseStudy}/${item.node.url}`}
									description={getFirstText(item.node.seo.meta_description)}
								/>
							))}
						</div>
						{limitCaseStudies < caseStudies.length && (
							<Link to={page.cta_show_more_button.url} className="button button--secondary button--xl">
								{page.cta_show_more_button.text}
							</Link>
						)}
					</div>
				</div>
				<div className="main__section main__section--100" id="guias">
					<div className="container">
						<div className="tabs tabs--pills-big">
							<div
								className={`tabs__tab ${selectedIndexSolution === 0 ? "tabs__tab--active" : ""}`}
								onClick={() => setSelectedIndexSolution(0)}
							>
								{page.guides_title}
							</div>
							<div
								className={`tabs__tab ${selectedIndexSolution === 2 ? "tabs__tab--active" : ""}`}
								onClick={() => setSelectedIndexSolution(2)}
							>
								eBooks
							</div>
							{webinars.length > 0 && (
								<div
									className={`tabs__tab ${selectedIndexSolution === 1 ? "tabs__tab--active" : ""}`}
									onClick={() => setSelectedIndexSolution(1)}
								>
									Webinars
								</div>
							)}
							<div
								className={`tabs__tab ${selectedIndexSolution === 3 ? "tabs__tab--active" : ""}`}
								onClick={() => setSelectedIndexSolution(3)}
							>
								Blog
							</div>
							<div
								className={`tabs__tab ${selectedIndexSolution === 4 ? "tabs__tab--active" : ""}`}
								onClick={() => setSelectedIndexSolution(4)}
							>
								{page.resources_title}
							</div>
						</div>
						<div className={`tabs__content  ${selectedIndexSolution === 0 ? "tabs__content--show" : ""}`}>
							<div className="grid-lg-2">
								{guides.slice(0, limitGuides).map((guide, index) => (
									<CardPost
										key={index}
										lang={lang}
										url={`${pathGuides}/${guide.node.url}`}
										image={guide.node.seo.image_2}
										name={guide.node.title}
										description={getFirstText(guide.node.seo.meta_description)}
										big
									/>
								))}
							</div>
							{limitGuides < guides.length && (
								<button className="button button--secondary button--xl" onClick={showMoreGuides}>
									{page.cta_button}
								</button>
							)}
						</div>
						<div className={`tabs__content  ${selectedIndexSolution === 1 ? "tabs__content--show" : ""}`}>
							<div className="grid-lg-2">
								{webinars.slice(0, limitWebinars).map((webinar, index) => (
									<CardPost
										key={index}
										lang={lang}
										url={`${pathWebinars}/${webinar.node.url}`}
										image={webinar.node.thumbnail_2}
										name={webinar.node.name}
										description={getFirstText(webinar.node.seo.meta_description)}
									/>
								))}
							</div>
							{limitWebinars < webinars.length && (
								<button
									className="button button--secondary button--xl"
									onClick={showMoreWebinars}
								>
									{page.cta_button}
								</button>
							)}
						</div>
						<div className={`tabs__content  ${selectedIndexSolution === 2 ? "tabs__content--show" : ""}`}>
							<div className="grid-lg-2">
								{whitePapers.slice(0, limitWhitePapers).map((item, index) => (
									<CardPost
										key={index}
										lang={lang}
										url={`${pathWhitePapers}/${item.node.url}`}
										image={item.node.seo.image_2}
										name={item.node.title}
										description={getFirstText(item.node.seo.meta_description)}
									/>
								))}
							</div>
							{limitWhitePapers < whitePapers.length && (
								<button className="button button--secondary button--xl" onClick={showMoreWhitePapers}>
									{page.cta_button}
								</button>
							)}
						</div>
						<div className={`tabs__content  ${selectedIndexSolution === 3 ? "tabs__content--show" : ""}`}>
							<div className="grid-lg-2">
								{posts.slice(0, limitPosts).map((item, index) => (
									<CardPost
										key={index}
										lang={lang}
										url={`${pathBlog}/${item.node.url}`}
										image={item.node.photo_2}
										name={item.node.name}
										description={getFirstText(item.node.seo.meta_description)}
									/>
								))}
							</div>
							{limitPosts < posts.length && (
								<button className="button button--secondary button--xl" onClick={showMorePosts}>
									{page.cta_button}
								</button>
							)}
						</div>
						<div className={`tabs__content  ${selectedIndexSolution === 4 ? "tabs__content--show" : ""}`}>
							<div className="grid-lg-2">
								{resources.slice(0, limitResources).map((resource, index) => (
									<CardPost
										key={index}
										lang={lang}
										url={`${pathGuides}/${resource.node.url}`}
										image={resource.node.seo.image_2}
										name={resource.node.title}
										description={getFirstText(resource.node.seo.meta_description)}
										big
									/>
								))}
							</div>
							<a className="button button--secondary button--xl" href={pathResources}>
								{page.cta_button}
							</a>
						</div>
					</div>
				</div>
				<Opinions lang={lang} />
				<Certifications />
				<Identity lang={lang} />
			</main>
		</NewLayout>
	);
};

export default KnowledgeCenter

export const KnowledgeCenterQuery = graphql`
	query ($langKey: String) {
		page: strapi2022KnowledgeCenter(locale: { eq: $langKey }) {
			seo {
				title
				meta_description
				image_2 {
					localFile {
						publicURL
					}
				}
			}
			title
			description
			cta_button
			cta_show_more_button {
				text
				url
			}
			guides_title
			resources_title
		}
		webinars: allStrapiWebinars(
			filter: { locale: { eq: $langKey } }
			sort: { fields: [published_at], order: [DESC] }
		) {
			edges {
				node {
					seo {
						title
						meta_description
					}
					url
					name
					description
					important
					order
					date
					time_start
					thumbnail_2 {
						name
						alternativeText
						localFile {
							childImageSharp {
								gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
				}
			}
		}
		whitePapers: allStrapiWhitePapers(
			filter: { locale: { eq: $langKey } }
			sort: { fields: [published_at], order: [DESC] }
		) {
			edges {
				node {
					seo {
						title
						meta_description
						image_2 {
							name
							alternativeText
							localFile {
								childImageSharp {
									gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
								}
							}
						}
					}
					url
					title
					description
					important
					order
				}
			}
		}
		posts: allStrapiBlogPosts(
			filter: { locale: { eq: $langKey } }
			sort: { fields: [creation_datetime], order: [DESC] }
		) {
			edges {
				node {
					seo {
						title
						meta_description
					}
					url
					name
					content
					photo_2 {
						name
						alternativeText
						localFile {
							childImageSharp {
								gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
				}
			}
		}
		caseStudies: allStrapiCaseStudy(
			filter: { locale: { eq: $langKey } }
			sort: { fields: [published_at], order: [DESC] }
		) {
			edges {
				node {
					seo {
						title
						meta_description
					}
					url
					title
					content {
						description
					}
					logo_new {
						name
						alternativeText
						localFile {
							childImageSharp {
								gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
							}
						}
					}
				}
			}
		}
		guides: allStrapiGenericLandingPage(
			filter: { locale: { eq: $langKey }, show_in_guides: { eq: true } }
			sort: { fields: [guide_order], order: [ASC] }
		) {
			edges {
				node {
					seo {
						meta_description
						image_2 {
							name
							localFile {
								childImageSharp {
									gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
								}
							}
						}
					}
					title
					description
					url
					important
					order
				}
			}
		}
		resources: allStrapiGenericLandingPage(
			filter: { locale: { eq: $langKey }, resource_highlight: { eq: true } }
			limit: 4
		) {
			edges {
				node {
					seo {
						meta_description
						image_2 {
							name
							localFile {
								childImageSharp {
									gatsbyImageData(width: 930, placeholder: BLURRED, formats: [WEBP])
								}
							}
						}
					}
					title
					description
					url
					important
					order
				}
			}
		}
		helpers: markdownRemark(frontmatter: { lang: { eq: $langKey }, name: { eq: "helpers" } }) {
			frontmatter {
				trust_us
				last_news
				blog_path
				about_us_path
				request_demo_path
			}
		}
	}
`;
